<script setup lang="ts">
import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { T } from '@tolgee/vue'
import router from '@/router'
import { RouteName, routePath } from '@/routes'
import { useModals } from '@/composables/modal-helper'
import { computed } from 'vue'
import { DAILY_TASK_ID } from '@/types/shared/player'
import { useConfig } from '@/composables/game/config'
import { usePathwayEventOverlay, usePathwayEvents } from '@/composables/game/pathway-events'
import type { PathwayEvent } from '@/types/shared/pathway-event'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import { useTaskList } from '@/composables/game/tasks'
import { useWalletConnect } from '@/composables/game/wallet-connect'

defineProps<{
  show: boolean,
}>()

const emit = defineEmits<{
  (e: 'hide'): void
}>()

const {
  setShowTransactionModal,
} = useModals()
const { config } = useConfig()
const {  pathwayEvents } = usePathwayEvents()
const { setSelectedPathwayEvent } = usePathwayEventOverlay()
const { open } = useTonConnectModal()
const { showBuyStarsModal } = useTaskList()
const { rawAddress } = useWalletConnect()

const levelUpTickets = 1

const tonTickets = computed(() => config.value.game.tasks.dailyTasks[DAILY_TASK_ID.TON_DAILY_CHECKIN].tickets)

function getTickets(tickets: number) {
  return tickets + `${tickets > 1 ? ' tickets' : ' ticket'}`
}

function onDepositStars() {
  console.log('here')
  showBuyStarsModal.value = true
  router.push(routePath[RouteName.EARN])
}

</script>
<template>
  <TheBottomSheet
    :show="show"
    content-class="pt-8"
    centered
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col gap-y-6 items-center">
      <img
        src="@/assets/images/gifs/ticket.gif"
        class="size-[100px]"
      >
      <p v-if="!rawAddress" class="text-base font-medium text-white/60 whitespace-pre-line">
        <span
          class="underline"
          @click="open
          "
        > <T
          key-name="giveaway.tickets.modal.connect"
          default-value="Connect"
        /></span>
        <T
          key-name="giveaway.tickets.modal.subtitle"
          default-value=" your wallet to enter the giveaway."
        />
      </p>
      <div class="flex flex-col gap-y-1 w-full">
        <div class="flex gap-x-1.5 text-solid-white text-lg -tracking-[0.36px] font-bold items-center">
          <img
            src="@/assets/images/fortune-wheel-without-border.png"
            class="size-6"
          >
          <T
            key-name="giveaway.tickets.modal.spins.title"
            default-value="Spin the wheel"
          />
        </div>
        <p class="text-base font-medium text-white/60 whitespace-pre-line">
          <span
            class="underline"
            @click="router.push(routePath[RouteName.REWARDS])"
          >Spin</span> to win <span class="text-green-primary">tickets</span> or find some from the treasure chest!
        </p>
      </div>
      <div class="flex flex-col gap-y-1 w-full">
        <div class="flex gap-x-1.5 text-solid-white text-lg -tracking-[0.36px] font-bold items-center">
          <img
            src="@/assets/images/emojis/emoji-arrow-up.png"
            class="size-6"
          >
          <T
            key-name="giveaway.tickets.modal.level_up.title"
            default-value="Level up"
          />
        </div>
        <p class="text-base font-medium text-white/60 whitespace-pre-line">
          Play, <span
            class="underline"
            @click="router.push(routePath[RouteName.MINE])"
          >upgrade your cards</span>, and level up to earn <span class="text-green-primary">{{ getTickets(levelUpTickets)
          }}</span> as a reward!
        </p>
      </div>
      <!--      <div-->
      <!--        class="flex flex-col gap-y-1 w-full"-->
      <!--      >-->
      <!--        <div class="flex gap-x-1.5 text-solid-white text-lg -tracking-[0.36px] font-bold items-center">-->
      <!--          <img-->
      <!--            src="@/assets/images/ton-symbol.png"-->
      <!--            class="size-6"-->
      <!--          >-->
      <!--          <T-->
      <!--            key-name="giveaway.tickets.modal.ton.title"-->
      <!--            default-value="Promote TON"-->
      <!--          />-->
      <!--        </div>-->
      <!--        <p class="text-base font-medium text-white/60 whitespace-pre-line">-->
      <!--          <span-->
      <!--            class="underline"-->
      <!--            @click="$emit('hide'); setShowTransactionModal(true)"-->
      <!--          >Make a small daily transaction on TON</span> and unlock <span class="text-green-primary">{{ getTickets(tonTickets)-->
      <!--          }}</span> on top of your rewards!-->
      <!--        </p>-->
      <!--      </div>-->
      <div
        class="flex flex-col gap-y-1 w-full"
      >
        <div class="flex gap-x-1.5 text-solid-white text-lg -tracking-[0.36px] font-bold items-center">
          <img
            src="@/assets/images/premium.png"
            class="size-6"
          >
          <T
            key-name="giveaway.tickets.modal.deposit_stars.title"
            default-value="Deposit Stars"
          />
        </div>
        <p class="text-base font-medium text-white/60 whitespace-pre-line">
          <span
            class="underline"
            @click="onDepositStars"
          > Deposit stars</span> into Dropee to unlock <span class="text-green-primary">more tickets</span> & rewards each day!
        </p>
      </div>
      <div
        class="flex flex-col gap-y-1 w-full"
      >
        <div class="flex gap-x-1.5 text-solid-white text-lg -tracking-[0.36px] font-bold items-center">
          <img
            src="@/assets/images/emojis/emoji-box.png"
            class="size-6"
          >
          <T
            key-name="giveaway.tickets.modal.mystery_box.title"
            default-value="Get a mystery box"
          />
        </div>
        <p class="text-base font-medium text-white/60 whitespace-pre-line">
          Open a <span
            class="underline"
            @click="router.push(routePath[RouteName.MYSTERY_BOX])"
          > mystery box</span>—you never know what surprises (or <span class="text-green-primary">tickets</span>!) you’ll get!
        </p>
      </div>
      <div
        class="flex flex-col gap-y-1 w-full"
      >
        <div class="flex gap-x-1.5 text-solid-white text-lg -tracking-[0.36px] font-bold items-center">
          <img
            src="@/assets/images/pathway-double-arrows.png"
            class="size-6 rotate-180"
          >
          <T
            key-name="giveaway.tickets.modal.pathway.title"
            default-value="Path to tickets"
          />
        </div>
        <p class="text-base font-medium text-white/60 whitespace-pre-line">
          Keep an eye out for <span
            class="underline"
            @click="emit('hide'); setSelectedPathwayEvent(pathwayEvents.find(event => !event.hidden) as PathwayEvent)"
          > special pathway events</span> on Home offering <span class="text-green-primary">tickets!</span>
        </p>
      </div>
    </div>
  </TheBottomSheet>
</template>
